import React, { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Paper, Stack, Typography, Divider, Box, Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Checkbox, FormControlLabel, Grid, Pagination, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import store from '../store';
import * as XLSX from 'xlsx';

const AdminReview = observer(() => {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(null);
  const [comment, setComment] = useState('');

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selected, setSelected] = useState({}); // id -> true
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  
  const allSelected = useMemo(() => store.adminBx.length > 0 && store.adminBx.every(r => selected[r.id]), [selected, store.adminBx]);
  const toggleAll = () => {
    if (allSelected) setSelected({});
    else {
      const m = {}; store.adminBx.forEach(r => m[r.id] = true); setSelected(m);
    }
  };

  const load = () => store.fetchAdminBx({ 
    start_date: startDate || undefined, 
    end_date: endDate || undefined,
    page: page,
    page_size: pageSize
  });
  
  useEffect(() => { load(); }, [page, pageSize]);
  const onFilter = (e) => { 
    e.preventDefault(); 
    setPage(1); // 重置到第一页
    load(); 
  };

  const openDialog = (r) => { setCurrent(r); setComment(''); setOpen(true); };
  const closeDialog = () => { setOpen(false); setCurrent(null); setComment(''); };
  const act = async (action) => {
    await store.reviewBX(current.id, action, comment);
    closeDialog();
    load();
    // 同时刷新申请数据，确保管理员能看到最新的申请状态
    if (store.currentUser && store.currentUser.role === 'admin') {
      store.fetchbx('mine');
    }
  };

  const exportExcel = (ids) => {
    const data = store.adminBx.filter(r => !ids || ids.length === 0 || ids.includes(r.id)).map(r => ({
      ID: r.id,
      出差所在地: r.travel_city || '',
      开始时间: r.start_time || '',
      结束时间: r.end_time || '',
      单据类型: r.document_type || '',
      费用类型: r.expense_type || '',
      计数量: r.quantity ?? '',
      进税项: r.input_tax_item || '',
      报销金额: r.reimburse_amount ?? '',
      不含税金额: r.amount_excl_tax ?? '',
      参与人: r.participants || '',
      发票号码: r.invoice_no || '',
      销售方名称: r.seller_name || '',
      状态: r.status,
      申请人: r.employee_name,
      创建时间: r.created_at,
      审核意见: r.review_comment || '',
      审核时间: r.reviewed_at || '',
      附件数: Array.isArray(r.attachments) ? r.attachments.length : 0,
      附件: Array.isArray(r.attachments) ? r.attachments.join(';') : '',
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '报销清单');
    XLSX.writeFile(wb, 'bx.xlsx');
  };

  return (
    <>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>管理员审核</Typography>
        <Divider sx={{ my: 2 }} />

        <Box component="form" onSubmit={onFilter} sx={{ mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}><TextField label="开始日期" type="date" InputLabelProps={{ shrink: true }} value={startDate} onChange={(e)=>setStartDate(e.target.value)} fullWidth /></Grid>
            <Grid item xs={12} sm={4}><TextField label="结束日期" type="date" InputLabelProps={{ shrink: true }} value={endDate} onChange={(e)=>setEndDate(e.target.value)} fullWidth /></Grid>
            <Grid item xs={12} sm={4}><Button type="submit" variant="contained">筛选</Button></Grid>
          </Grid>
        </Box>

        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
          <FormControlLabel control={<Checkbox checked={allSelected} onChange={toggleAll} />} label="全选" />
          <Button size="small" variant="outlined" onClick={() => exportExcel(Object.keys(selected).filter(k=>selected[k]).map(Number))}>导出Excel</Button>
        </Stack>
        <Stack spacing={1}>
          {store.adminBx.map((r) => (
            <Box key={r.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Checkbox checked={!!selected[r.id]} onChange={(e)=>setSelected(prev=>({ ...prev, [r.id]: e.target.checked }))} />
                <Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" sx={{ cursor: 'pointer' }} onClick={()=>openDialog(r)}>#{r.id} {r.document_type} - {r.expense_type} - ¥{r.reimburse_amount}</Typography>
                    {(() => {
                      const color = r.status === 'pending' ? '#1976d2' : (r.status === 'approved' ? '#2e7d32' : '#d32f2f');
                      const bg = r.status === 'pending' ? 'rgba(25,118,210,0.12)' : (r.status === 'approved' ? 'rgba(46,125,50,0.12)' : 'rgba(211,47,47,0.12)');
                      const text = r.status === 'pending' ? '待审核' : (r.status === 'approved' ? '已通过' : '已驳回');
                      return <Box sx={{ px: 1, py: 0.25, borderRadius: 1, bgcolor: bg, color, fontSize: 12 }}>{text}</Box>;
                    })()}
                  </Stack>
                  <Typography variant="caption" color="text.secondary">申请人：{r.employee_name} / {r.created_at}</Typography>
                </Stack>
              </Stack>
              <Stack direction="row" spacing={1}>
                <Button size="small" variant="outlined" onClick={() => openDialog(r)}>审核</Button>
              </Stack>
            </Box>
          ))}
          {store.adminBx.length === 0 && <Typography variant="body2" color="text.secondary">暂无数据</Typography>}
        </Stack>
        
        {/* 分页组件 */}
        {store.adminPagination.totalPages > 1 && (
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                共 {store.adminPagination.total} 条记录，第 {store.adminPagination.page} / {store.adminPagination.totalPages} 页
              </Typography>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>每页条数</InputLabel>
                <Select
                  value={pageSize}
                  label="每页条数"
                  onChange={(e) => {
                    setPageSize(e.target.value);
                    setPage(1);
                  }}
                >
                  <MenuItem value={10}>10条</MenuItem>
                  <MenuItem value={25}>25条</MenuItem>
                  <MenuItem value={50}>50条</MenuItem>
                  <MenuItem value={100}>100条</MenuItem>
                </Select>
              </FormControl>
            </Stack>
            <Pagination
              count={store.adminPagination.totalPages}
              page={page}
              onChange={(e, newPage) => setPage(newPage)}
              showFirstButton
              showLastButton
              color="primary"
            />
          </Box>
        )}
      </Paper>

      <Dialog open={open} onClose={closeDialog} fullWidth maxWidth="md">
        <DialogTitle>审核报销 #{current?.id}</DialogTitle>
        <DialogContent dividers>
              {current && (
                <Stack spacing={2} sx={{ mb: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}><Typography variant="body2">出差所在地：{current.travel_city || '-'}</Typography></Grid>
                    <Grid item xs={12} sm={4}><Typography variant="body2">开始时间：{current.start_time || '-'}</Typography></Grid>
                    <Grid item xs={12} sm={4}><Typography variant="body2">结束时间：{current.end_time || '-'}</Typography></Grid>
                    <Grid item xs={12} sm={4}><Typography variant="body2">单据类型：{current.document_type || '-'}</Typography></Grid>
                    <Grid item xs={12} sm={4}><Typography variant="body2">费用类型：{current.expense_type || '-'}</Typography></Grid>
                    <Grid item xs={12} sm={4}><Typography variant="body2">计数量：{current.quantity ?? '-'}</Typography></Grid>
                    <Grid item xs={12} sm={4}><Typography variant="body2">进税项：{current.input_tax_item || '-'}</Typography></Grid>
                    <Grid item xs={12} sm={4}><Typography variant="body2">报销金额：{current.reimburse_amount}</Typography></Grid>
                    <Grid item xs={12} sm={4}><Typography variant="body2">不含税金额：{current.amount_excl_tax ?? '-'}</Typography></Grid>
                    <Grid item xs={12} sm={4}><Typography variant="body2">参与人：{current.participants || '-'}</Typography></Grid>
                    <Grid item xs={12} sm={4}><Typography variant="body2">发票号码：{current.invoice_no || '-'}</Typography></Grid>
                    <Grid item xs={12} sm={4}><Typography variant="body2">销售方名称：{current.seller_name || '-'}</Typography></Grid>
                    <Grid item xs={12} sm={4}><Typography variant="body2">状态：{current.status}</Typography></Grid>
                    <Grid item xs={12}><Typography variant="body2">说明：{current.description || '-'}</Typography></Grid>
                    <Grid item xs={12} sm={4}><Typography variant="body2">申请人：{current.employee_name}</Typography></Grid>
                    <Grid item xs={12} sm={4}><Typography variant="body2">创建时间：{current.created_at}</Typography></Grid>
                    <Grid item xs={12}><Divider /></Grid>
                    {Array.isArray(current.attachments) && current.attachments.length > 0 && (
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" gutterBottom>附件</Typography>
                        <Stack spacing={1}>
                          {current.attachments.map((fn, idx) => (
                            <Box key={fn + idx}>
                              <a href={`/api/uploads/${fn}`} target="_blank" rel="noreferrer">
                                <Typography variant="body2" sx={{ cursor: 'pointer', textDecoration: 'underline' }}>{fn}</Typography>
                              </a>
                            </Box>
                          ))}
                        </Stack>
                      </Grid>
                    )}
                    <Grid item xs={12}><Divider /></Grid>
                    <Grid item xs={12} sm={6}><Typography variant="body2">审核意见：{current.review_comment || '-'}</Typography></Grid>
                    <Grid item xs={12} sm={6}><Typography variant="body2">审核时间：{current.reviewed_at || '-'}</Typography></Grid>
                  </Grid>
                  <Divider />
                  <TextField label="审核意见" fullWidth multiline rows={3} value={comment} onChange={(e) => setComment(e.target.value)} />
                </Stack>
              )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>取消</Button>
          <Button variant="contained" color="success" onClick={() => act('approve')}>通过</Button>
          <Button variant="contained" color="error" onClick={() => act('reject')}>驳回</Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

export default AdminReview;


